import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { UserRole, useApi, useTenantContext } from '../lib/api';

type TenantUserStatus = 'ACTIVE' | 'INVITED' | 'DISABLED';

type TenantUser = {
  user_id: string;
  role: UserRole;
  status: TenantUserStatus;
  email?: string | null;
  display_name?: string | null;
  created_at: string;
  updated_at: string;
};

const ROLES: UserRole[] = ['OWNER', 'ADMIN', 'SUPERVISOR', 'AGENT', 'VIEWER'];
const STATUSES: TenantUserStatus[] = ['ACTIVE', 'INVITED', 'DISABLED'];

function emptyForm() {
  return {
    user_id: '',
    role: 'AGENT' as UserRole,
    status: 'ACTIVE' as TenantUserStatus,
    email: '',
    display_name: '',
    reason: '',
  };
}

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString('pt-BR') : '-';
}

export default function UsersPage() {
  const { tenant_id, hasScope } = useTenantContext();
  const { request } = useApi();
  const [items, setItems] = useState<TenantUser[]>([]);
  const [form, setForm] = useState(emptyForm());
  const [deleteReason, setDeleteReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const canAdminUsers = hasScope('USERS_ADMIN');

  const load = useCallback(() => {
    if (!tenant_id || !canAdminUsers) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    request<{ items: TenantUser[] }>(`/tenants/${encodeURIComponent(tenant_id)}/users`)
      .then((out) => {
        if (!cancelled) setItems(out.items);
      })
      .catch((err) => {
        if (!cancelled) setError(String(err instanceof Error ? err.message : err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [canAdminUsers, request, tenant_id]);

  useEffect(() => {
    return load();
  }, [load]);

  async function submitUser(event: FormEvent) {
    event.preventDefault();
    if (!tenant_id || !canAdminUsers) return;
    setError(null);
    setSuccess(null);
    setSaving('upsert');
    try {
      const out = await request<{ items: TenantUser[] }>(
        `/tenants/${encodeURIComponent(tenant_id)}/users`,
        {
          method: 'POST',
          body: JSON.stringify({
            user_id: form.user_id,
            role: form.role,
            status: form.status,
            email: form.email.trim() || null,
            display_name: form.display_name.trim() || null,
            reason: form.reason,
          }),
        },
      );
      setItems(out.items);
      setForm(emptyForm());
      setSuccess('Usuario atualizado.');
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(null);
    }
  }

  async function deleteUser(user_id: string) {
    if (!tenant_id || !deleteReason.trim()) return;
    setError(null);
    setSuccess(null);
    setSaving(`delete:${user_id}`);
    try {
      const out = await request<{ items: TenantUser[] }>(
        `/tenants/${encodeURIComponent(tenant_id)}/users/${encodeURIComponent(user_id)}`,
        {
          method: 'DELETE',
          body: JSON.stringify({ reason: deleteReason }),
        },
      );
      setItems(out.items);
      setSuccess('Usuario removido.');
    } catch (err) {
      setError(String(err instanceof Error ? err.message : err));
    } finally {
      setSaving(null);
    }
  }

  if (!tenant_id) return <main className="page">Usuario sem tenant configurado.</main>;
  if (!canAdminUsers) return <main className="page">Sem permissao para usuarios.</main>;

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Usuarios</h1>
          <p>Tenant {tenant_id}</p>
        </div>
        <div className="header-actions">
          {hasScope('CONFIG_CHANGE') ? (
            <>
              <Link className="nav-link" to="/config">
                Configuracao
              </Link>
              <Link className="nav-link" to="/simulation">
                Simulacao
              </Link>
            </>
          ) : null}
          <Link className="nav-link" to="/">
            Inbox
          </Link>
        </div>
      </header>

      {loading ? <p className="hint">Carregando usuarios...</p> : null}
      {error ? <p className="error">{error}</p> : null}
      {success ? <p className="success">{success}</p> : null}

      <section className="admin-layout">
        <form className="panel admin-form" onSubmit={submitUser}>
          <h2>Adicionar ou atualizar</h2>
          <label>
            User ID
            <input
              value={form.user_id}
              onChange={(event) => setForm({ ...form, user_id: event.target.value })}
            />
          </label>
          <label>
            Papel
            <select
              value={form.role}
              onChange={(event) => setForm({ ...form, role: event.target.value as UserRole })}
            >
              {ROLES.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>
          <label>
            Status
            <select
              value={form.status}
              onChange={(event) =>
                setForm({ ...form, status: event.target.value as TenantUserStatus })
              }
            >
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </label>
          <label>
            Email
            <input
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          </label>
          <label>
            Nome
            <input
              value={form.display_name}
              onChange={(event) => setForm({ ...form, display_name: event.target.value })}
            />
          </label>
          <label>
            Motivo
            <textarea
              value={form.reason}
              onChange={(event) => setForm({ ...form, reason: event.target.value })}
              rows={3}
            />
          </label>
          <button
            type="submit"
            disabled={!form.user_id.trim() || !form.reason.trim() || saving === 'upsert'}
          >
            {saving === 'upsert' ? 'Salvando...' : 'Salvar usuario'}
          </button>
        </form>

        <section className="panel admin-table">
          <h2>Memberships</h2>
          <label>
            Motivo para remocao
            <input value={deleteReason} onChange={(event) => setDeleteReason(event.target.value)} />
          </label>
          {items.length ? (
            <table>
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Papel</th>
                  <th>Status</th>
                  <th>Atualizado</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((user) => (
                  <tr key={user.user_id}>
                    <td>{user.display_name ?? user.email ?? user.user_id}</td>
                    <td>{user.role}</td>
                    <td>{user.status}</td>
                    <td>{formatDate(user.updated_at)}</td>
                    <td>
                      <button
                        type="button"
                        className="danger"
                        disabled={!deleteReason.trim() || saving === `delete:${user.user_id}`}
                        onClick={() => void deleteUser(user.user_id)}
                      >
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="hint">Nenhum usuario registrado.</p>
          )}
        </section>
      </section>
    </main>
  );
}
